import React, { useState, useEffect } from 'react';
import { adminGetMembers, adminRecordAttendance, adminGetAttendanceRecords, adminDeleteAttendance, adminSetAttendanceReward, getAttendance } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, CalendarCheck, Trophy, Star, ChevronLeft, ChevronRight, Gift } from 'lucide-react';

const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const AdminAttendance = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [members, setMembers] = useState([]);
  const [records, setRecords] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [form, setForm] = useState({ user_id: '', date: '', type: 'training' });
  const [rewardText, setRewardText] = useState('');

  const fetchData = async () => {
    try {
      const [membersRes, recordsRes, attRes] = await Promise.all([
        adminGetMembers(),
        adminGetAttendanceRecords(month, year),
        getAttendance(month, year),
      ]);
      setMembers(membersRes.data);
      setRecords(recordsRes.data);
      setAttendanceData(attRes.data);
    } catch (e) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [month, year]);

  const handleRecord = async () => {
    if (!form.user_id || !form.date) {
      toast.error('Bitte Mitglied und Datum auswählen');
      return;
    }
    try {
      await adminRecordAttendance(form);
      toast.success('Anwesenheit erfasst');
      setAddDialogOpen(false);
      setForm({ user_id: '', date: '', type: 'training' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      await adminDeleteAttendance(id);
      toast.success('Gelöscht');
      fetchData();
    } catch (e) { toast.error('Fehler'); }
  };

  const handleSetReward = async () => {
    const best = attendanceData?.monthly_best;
    if (!best) {
      toast.error('Kein Monatsbester vorhanden');
      return;
    }
    try {
      await adminSetAttendanceReward({
        month, year,
        user_id: best.user_id,
        reward: rewardText,
      });
      toast.success('Belohnung gespeichert');
      setRewardDialogOpen(false);
      setRewardText('');
      fetchData();
    } catch (e) { toast.error('Fehler'); }
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else { setMonth(m => m - 1); }
  };

  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else { setMonth(m => m + 1); }
  };

  const best = attendanceData?.monthly_best;
  // Merge admin user into selectable list
  const allUsers = [...members];

  return (
    <div data-testid="admin-attendance-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-bold uppercase">Anwesenheit</h1>
        <div className="flex gap-2">
          {best && (
            <Button onClick={() => { setRewardText(attendanceData?.reward?.reward || ''); setRewardDialogOpen(true); }} className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30" data-testid="set-reward-btn">
              <Gift className="w-4 h-4 mr-2" />Belohnung
            </Button>
          )}
          <Button onClick={() => { setForm({ user_id: '', date: new Date().toISOString().split('T')[0], type: 'training' }); setAddDialogOpen(true); }} className="btn-primary" data-testid="add-attendance-btn">
            <Plus className="w-4 h-4 mr-2" />Erfassen
          </Button>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
        <span className="font-heading font-bold uppercase text-lg">{MONTHS[month - 1]} {year}</span>
        <Button variant="ghost" size="sm" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Monthly Best */}
      {best && (
        <Card className="card-elevated border-yellow-500/30 mb-6" data-testid="admin-monthly-best">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Trophy className="w-6 h-6 text-yellow-500" />
              <div>
                <p className="text-sm text-yellow-400 font-bold">Monatsbester</p>
                <p className="font-heading font-bold">{best.name} - {best.count} Teilnahmen</p>
              </div>
            </div>
            {attendanceData?.reward && (
              <Badge className="bg-yellow-500/20 text-yellow-400">
                <Star className="w-3 h-3 mr-1" />{attendanceData.reward.reward}
              </Badge>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary per Member */}
      {attendanceData?.members?.length > 0 && (
        <Card className="card-surface mb-6">
          <CardContent className="p-4">
            <h3 className="font-heading font-bold uppercase text-sm mb-3">Übersicht {MONTHS[month - 1]}</h3>
            <div className="space-y-2">
              {attendanceData.members.map((m, i) => (
                <div key={m.user_id} className={`flex items-center justify-between p-2 rounded ${best && m.user_id === best.user_id && m.count > 0 ? 'bg-yellow-500/10' : 'bg-white/[0.02]'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-text-secondary text-sm w-6">{i + 1}.</span>
                    <span className={`font-medium text-sm ${best && m.user_id === best.user_id && m.count > 0 ? 'text-yellow-400' : ''}`}>{m.name}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">{m.count} Teilnahmen</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Records */}
      <h3 className="font-heading font-bold uppercase text-sm mb-3">Einzelne Einträge</h3>
      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map(i => <Card key={i} className="card-surface animate-pulse"><CardContent className="p-4"><div className="h-10 bg-white/10 rounded" /></CardContent></Card>)}</div>
      ) : records.length > 0 ? (
        <div className="space-y-2">
          {records.map(rec => (
            <Card key={rec.attendance_id} className="card-surface">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CalendarCheck className="w-4 h-4 text-accent" />
                  <div>
                    <p className="font-medium text-sm">{rec.user_name || rec.user_id}</p>
                    <p className="text-xs text-text-secondary">{formatDate(rec.date)} &middot; {rec.type === 'tournament' ? 'Turnier' : 'Training'}</p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(rec.attendance_id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-surface">
          <CardContent className="p-8 text-center">
            <CalendarCheck className="w-12 h-12 text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">Keine Einträge für {MONTHS[month - 1]} {year}.</p>
          </CardContent>
        </Card>
      )}

      {/* Add Attendance Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle>Anwesenheit erfassen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Mitglied *</Label>
              <Select value={form.user_id} onValueChange={v => setForm({ ...form, user_id: v })}>
                <SelectTrigger className="input-dark" data-testid="attendance-member-select"><SelectValue placeholder="Mitglied wählen" /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10 max-h-60">
                  {(attendanceData?.members || []).map(m => (
                    <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Datum *</Label>
              <Input type="date" className="input-dark" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="attendance-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Art</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10">
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="tournament">Turnier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleRecord} className="btn-primary w-full" data-testid="save-attendance-btn">Erfassen</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reward Dialog */}
      <Dialog open={rewardDialogOpen} onOpenChange={setRewardDialogOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle>Belohnung für Monatsbesten</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {best && (
              <div className="card-elevated p-4 flex items-center gap-3">
                <Trophy className="w-6 h-6 text-yellow-500" />
                <div>
                  <p className="font-bold">{best.name}</p>
                  <p className="text-sm text-text-secondary">{best.count} Teilnahmen in {MONTHS[month - 1]}</p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Belohnung / Überraschung</Label>
              <Input className="input-dark" placeholder="z.B. Getränk frei, Pokal, Gutschein..." value={rewardText} onChange={e => setRewardText(e.target.value)} data-testid="reward-input" />
            </div>
            <Button onClick={handleSetReward} className="btn-primary w-full" data-testid="save-reward-btn">Belohnung speichern</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAttendance;
